import { Component } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngxs/store';
import { UntilDestroy } from '@ngneat/until-destroy';

import {
    AlertService,
    ButtonColors,
    ButtonModule,
    LayerService,
    TextfieldModule,
} from '@ed---interne/ng-uui-components';
import { EmploymentStatus, User } from '../../../../core/models/user.model';
import { CreateUser } from '../../../../features/admin/users/users.state';
import { emailValidator } from '../../../../shared/validators/email.validator';
import { passwordValidator } from '../../../../shared/validators/password.validator';
import { Organization } from '../../../../core/models/organization.model';
import { CommonModule } from '@angular/common';
import { SharedModule } from 'src/app/shared/shared.module';

@UntilDestroy()
@Component({
    selector: 'user-create',
    templateUrl: './user-create.component.html',
    styleUrls: ['./user-create.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        SharedModule,
        TextfieldModule,
        ButtonModule,
        ReactiveFormsModule,
        FormsModule,
    ],
})
export class UserCreateComponent {
    public buttonColors = ButtonColors;
    public employmentStatusList = Object.values(EmploymentStatus).map((value) => ({
        value,
        label: value,
    }));

    public user!: User;
    public password: string | null = null;
    public passwordConfirm: string | null = null;
    public header: any;
    public organizationId: any;
    public userFormGroup: FormGroup = this.formBuilder.group({
        email: [{ value: '', disabled: false }, [emailValidator()]],
        password: ['', [passwordValidator()]],
        firstname: [''],
        lastname: [''],
        passwordConfirm: ['', [passwordValidator()]],
        employmentStatus: [EmploymentStatus.PROJECT_WORKER],
        isAdmin: false,
        payfitContractId: [''],
    });

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly layerService: LayerService,
        private readonly store: Store,
        private readonly alertService: AlertService,
    ) {
        this.userFormGroup = this.formBuilder.group({
            email: [{ value: '', disabled: false }, [emailValidator()]],
            password: ['', [passwordValidator()]],
            firstname: [''],
            lastname: [''],
            passwordConfirm: ['', [passwordValidator()]],
            employmentStatus: [EmploymentStatus.PROJECT_WORKER],
            isAdmin: [false],
            payfitContractId: [''],
        });
    }

    public valid(): void {
        const orgaToCreate: Partial<Organization> = { id: this.organizationId };
        const userToUpdate: Partial<User> = {
            mail: this.userFormGroup.value.email as string,
            firstname: this.userFormGroup.value.firstname as string,
            lastname: this.userFormGroup.value.lastname as string,
            isAdmin: !!this.userFormGroup.value.isAdmin,
            isActive: false,
            organization: orgaToCreate,
            employmentStatus: this.userFormGroup.value.employmentStatus,
            payfitContractId: this.userFormGroup.value.payfitContractId,
        };
        //create
        this.store.dispatch(new CreateUser({ user: userToUpdate })).subscribe({
            next: () => {
                this.alertService.valid('Utilisateurs', 'Utilisateur créé !');
                this.close();
            },
            error: (err: any) => {
                console.info(err.error.message);
                this.alertService.error('Création', err.error.message);
            },
        });
    }

    updateEmploymentStatus(employmentStatus: EmploymentStatus) {
        this.userFormGroup.patchValue({ employmentStatus });
    }

    isMail(input: string): boolean {
        const expression: RegExp = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
        return expression.test(input);
    }

    public set params(params: { header: any; user: User; organizationId: any }) {
        this.header = params.header;
        this.organizationId = params.organizationId;

        this.userFormGroup.patchValue({
            email: '',
            firstname: '',
            lastname: '',
            isAdmin: '',
            password: '',
            passwordConfirm: '',
            employmentStatus: EmploymentStatus.PROJECT_WORKER,
        });
    }

    public close(): void {
        this.layerService.close();
    }

    public canCreateEdit(): boolean {
        return this.userFormGroup.invalid || !this.userFormGroup.dirty;
    }

    public showPopupOnLayerClose(): boolean {
        return this.userFormGroup.dirty;
    }
}
